import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { logger } from '../../utils/logger';
import { notificationKeys } from './notificationKeys';
import {
  Notification,
  NotificationOperationResult,
  NotificationListResult,
  MarkAsReadInput,
} from './notificationTypes';
import { useQueryErrorHandler } from '../useQueryErrorHandler';
import { invalidateQuery } from '../../utils/queryInvalidationUtils';
import { db } from '../../lib/offline-db';
import { queueOfflineOperation } from '../../lib/offline-sync';
import { getConnectionState } from '../../contexts/ConnectionStateContext';

/**
 * Hook for fetching user notifications
 * Returns notifications list and unread count
 * Supports offline mode by using IndexedDB when offline
 *
 * @param options - Optional configuration object
 * @param options.showLoading - Whether to show loading state, defaults to true
 * @returns Query object with notifications data and loading state
 */
export function useNotifications(options?: { showLoading?: boolean }) {
  const { handleError } = useQueryErrorHandler();
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const showLoading = options?.showLoading ?? true; // Default to true with clearer syntax

  return useQuery({
    queryKey: notificationKeys.lists(),
    queryFn: async (): Promise<NotificationListResult> => {
      const fetchId = Date.now().toString().slice(-4);
      logger.fetch(`Starting notifications fetch #${fetchId} (online: ${isOnline})`);
      const startTime = performance.now();

      try {
        // If offline, get data from IndexedDB
        if (!isOnline) {
          logger.fetch(`Offline mode: fetching notifications from IndexedDB #${fetchId}`);

          try {
            // Get notifications from IndexedDB
            const notifications = await db.notifications
              .orderBy('created_at_ms') // Use numeric timestamp for reliable sorting
              .reverse()
              .filter(note => !note.isDeleted) // Filter out any soft-deleted notifications
              .toArray();

            logger.fetch(
              `Offline mode: loaded ${notifications.length} notifications from IndexedDB`
            );

            // Calculate unread count
            const unreadCount = notifications.filter(n => !n.is_read).length;

            return {
              notifications: notifications as Notification[],
              unreadCount,
            };
          } catch (offlineError) {
            logger.error(`Error fetching offline notifications #${fetchId}:`, offlineError);
            throw new Error('Failed to load notifications from offline storage');
          }
        }

        // Online flow - fetch from server
        const supabaseClient = await supabase;

        // Get all notifications for the current user, sorted by most recent first
        const { data, error } = await supabaseClient
          .from('notifications')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) throw error;

        // Extract notifications and calculate unread count
        const notifications = data as Notification[];
        const unreadCount = notifications.filter(n => !n.is_read).length;

        // Store in IndexedDB for offline access
        if (notifications && notifications.length > 0) {
          try {
            // Add created_at_ms field to each notification
            const processedNotifications = notifications.map(notification => ({
              ...notification,
              created_at_ms: notification.created_at
                ? new Date(notification.created_at).getTime()
                : 0,
            }));

            await db.notifications.bulkPut(processedNotifications);
            logger.fetch(`Stored ${notifications.length} notifications in IndexedDB`);
          } catch (storageError) {
            logger.error(`Error storing notifications in IndexedDB:`, storageError);
            // Continue even if storage fails - this is non-critical
          }
        }

        // Log performance metrics
        const endTime = performance.now();
        const duration = endTime - startTime;

        logger.fetch(
          showLoading
            ? `Notifications fetch #${fetchId} completed in ${duration.toFixed(2)}ms`
            : `Notifications silently refreshed #${fetchId} in ${duration.toFixed(2)}ms`,
          {
            count: notifications.length,
            unreadCount,
            silent: !showLoading,
            duration: duration.toFixed(2),
          }
        );

        return { notifications, unreadCount };
      } catch (error) {
        const appError = handleError(error, {
          action: showLoading ? 'toast' : 'silent',
          context: {
            operation: 'fetchNotifications',
            silent: !showLoading,
            isOnline,
          },
        });
        throw appError;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook for marking a notification as read
 * Updates the notification in the database and updates the cache
 * Supports offline mode by updating IndexedDB and queueing for sync
 *
 * @returns Mutation hook for marking notifications as read
 */
export function useMarkNotificationAsRead() {
  const queryClient = useQueryClient();
  const { handleError } = useQueryErrorHandler();
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;

  return useMutation({
    mutationFn: async ({
      notificationId,
    }: MarkAsReadInput): Promise<NotificationOperationResult> => {
      try {
        logger.store('Marking notification as read', {
          notificationId,
          is_online: isOnline,
        });

        const now = new Date().toISOString();

        // If offline, update locally and queue for sync
        if (!isOnline) {
          logger.store('Offline mode: Marking notification as read locally', { notificationId });

          try {
            // Get current notification from IndexedDB
            const notification = await db.notifications.get(notificationId);

            if (!notification) {
              throw new Error(`Notification ${notificationId} not found in offline storage`);
            }

            // Update the notification
            const updatedNotification = {
              ...notification,
              is_read: true,
              read_at: now,
            };

            // Save to IndexedDB
            await db.notifications.put(updatedNotification);

            // Queue for sync when online
            await queueOfflineOperation('notifications', 'update', notificationId, {
              is_read: true,
              read_at: now,
            });

            logger.store('Notification marked as read locally', { notificationId });

            return { success: true, notificationId };
          } catch (offlineError) {
            const appError = handleError(offlineError, {
              action: 'toast',
              context: {
                operation: 'markNotificationAsRead',
                notificationId,
                mode: 'offline',
              },
            });
            throw appError;
          }
        }

        // Online flow
        const supabaseClient = await supabase;

        // Update the notification in the database
        const { error } = await supabaseClient
          .from('notifications')
          .update({
            is_read: true,
            read_at: now,
          })
          .eq('id', notificationId);

        if (error) throw error;

        // Update the notification in IndexedDB for offline access
        try {
          // Get current notification from IndexedDB
          const notification = await db.notifications.get(notificationId);

          if (notification) {
            // Update the notification
            await db.notifications.update(notificationId, {
              is_read: true,
              read_at: now,
            });

            logger.fetch(`Updated notification ${notificationId} in IndexedDB`);
          }
        } catch (storageError) {
          logger.error(`Error updating notification in IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
        }

        logger.component('Notification marked as read', { notificationId });

        return { success: true, notificationId };
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: {
            operation: 'markNotificationAsRead',
            notificationId: (error as any).notificationId || 'unknown',
            isOnline,
          },
        });
        throw appError;
      }
    },

    onMutate: async ({ notificationId }: MarkAsReadInput) => {
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: notificationKeys.lists() });

      // Get snapshot of current notifications data
      const previousData = queryClient.getQueryData<NotificationListResult>(
        notificationKeys.lists()
      );

      if (previousData) {
        const now = new Date().toISOString();

        // Update the notifications optimistically
        const updatedNotifications = previousData.notifications.map(notification =>
          notification.id === notificationId
            ? {
                ...notification,
                is_read: true,
                read_at: now,
              }
            : notification
        );

        const updatedUnreadCount = updatedNotifications.filter(n => !n.is_read).length;

        // Update the query cache
        queryClient.setQueryData<NotificationListResult>(notificationKeys.lists(), {
          notifications: updatedNotifications,
          unreadCount: updatedUnreadCount,
        });
      }

      return { previousData };
    },

    onError: (error, variables, context) => {
      // Log the error
      logger.error('Failed to mark notification as read', {
        error: error instanceof Error ? error.message : String(error),
        notificationId: variables.notificationId,
      });

      // Revert to the previous state if the mutation fails
      if (context?.previousData) {
        queryClient.setQueryData(notificationKeys.lists(), context.previousData);
      }
    },

    onSuccess: (result, variables) => {
      // Update the individual notification in the cache
      invalidateQuery(queryClient, notificationKeys.detail(variables.notificationId), {
        refetchType: 'none',
        context: { operation: 'markNotificationAsRead' },
      });

      // The lists() data has already been updated optimistically,
      // so we don't need to invalidate it
    },
  });
}

/**
 * Hook for marking all notifications as read
 * Updates all notifications in the database and updates the cache
 * Supports offline mode by updating IndexedDB and queueing for sync
 *
 * @returns Mutation hook for marking all notifications as read
 */
export function useMarkAllNotificationsAsRead() {
  const queryClient = useQueryClient();
  const { handleError } = useQueryErrorHandler();
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;

  return useMutation({
    mutationFn: async (): Promise<{ success: boolean }> => {
      try {
        logger.store('Marking all notifications as read', {
          is_online: isOnline,
        });

        const now = new Date().toISOString();

        // If offline, update locally and queue for sync
        if (!isOnline) {
          logger.store('Offline mode: Marking all notifications as read locally');

          try {
            // Get all unread notifications from IndexedDB
            const unreadNotifications = await db.notifications
              .where('is_read')
              .equals(false)
              .toArray();

            // Update each notification
            const updatePromises = unreadNotifications.map(async notification => {
              // Update in IndexedDB
              await db.notifications.update(notification.id, {
                is_read: true,
                read_at: now,
              });

              // Queue for sync when online
              await queueOfflineOperation('notifications', 'update', notification.id, {
                is_read: true,
                read_at: now,
              });
            });

            // Wait for all updates to complete
            await Promise.all(updatePromises);

            logger.store('All notifications marked as read locally', {
              count: unreadNotifications.length,
            });

            return { success: true };
          } catch (offlineError) {
            const appError = handleError(offlineError, {
              action: 'toast',
              context: {
                operation: 'markAllNotificationsAsRead',
                mode: 'offline',
              },
            });
            throw appError;
          }
        }

        // Online flow
        const supabaseClient = await supabase;

        // Update all notifications for the current user
        const { error, data } = await supabaseClient
          .from('notifications')
          .update({
            is_read: true,
            read_at: now,
          })
          .eq('is_read', false)
          .select('id');

        if (error) throw error;

        // Update the notifications in IndexedDB for offline access
        try {
          // Get all unread notifications from IndexedDB
          const unreadNotifications = await db.notifications
            .where('is_read')
            .equals(false)
            .toArray();

          // Update each notification
          const updatePromises = unreadNotifications.map(notification =>
            db.notifications.update(notification.id, {
              is_read: true,
              read_at: now,
            })
          );

          // Wait for all updates to complete
          await Promise.all(updatePromises);

          logger.fetch(`Updated ${unreadNotifications.length} notifications in IndexedDB`);
        } catch (storageError) {
          logger.error(`Error updating notifications in IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
        }

        logger.component('All notifications marked as read', {
          count: data?.length || 'unknown',
        });

        return { success: true };
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: {
            operation: 'markAllNotificationsAsRead',
            isOnline,
          },
        });
        throw appError;
      }
    },

    onMutate: async () => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: notificationKeys.lists() });

      // Get snapshot of current notifications
      const previousData = queryClient.getQueryData<NotificationListResult>(
        notificationKeys.lists()
      );

      if (previousData) {
        // Update all notifications optimistically
        const now = new Date().toISOString();
        const updatedNotifications = previousData.notifications.map(notification => ({
          ...notification,
          is_read: true,
          read_at: notification.read_at || now,
        }));

        // Update the query cache
        queryClient.setQueryData<NotificationListResult>(notificationKeys.lists(), {
          notifications: updatedNotifications,
          unreadCount: 0,
        });
      }

      return { previousData };
    },

    onError: (error, variables, context) => {
      // Log the error
      logger.error('Failed to mark all notifications as read', {
        error: error instanceof Error ? error.message : String(error),
      });

      // Revert to the previous state if the mutation fails
      if (context?.previousData) {
        queryClient.setQueryData(notificationKeys.lists(), context.previousData);
      }
    },

    onSuccess: () => {
      // The lists() data has already been updated optimistically,
      // so we only need to invalidate the unread count
      invalidateQuery(queryClient, notificationKeys.unreadCount(), {
        refetchType: 'none',
        context: { operation: 'markAllNotificationsAsRead' },
      });
    },
  });
}
